import {defineStore} from "pinia";
import {computed, ref} from "vue";
import {ShoppingCartPaymentStatus, ShoppingCartPaymentStatusResult} from '@/api/klip-api.proxy';

export enum PaymentType {
    Online,
    Topup,
}

export interface IPaymentData {
    paymentType: PaymentType;
    numberOfMapRequests?: number;
    hasMapRequestExtras?: boolean;
    hasMapRequestsWithImkl?: boolean;
    totalPrice?: number;
    vatLabel?: string;
}

export const usePaymentStatusStore = defineStore('payment-status-store', () => {
    const onlinePayment = ref<IPaymentData>(null);
    const onlinePaymentError = ref<boolean>(false);
    const topUpPaymentError = ref<boolean>(false);
    const unCertainTopUpPaymentError = ref<boolean>(false);
    const mapRequestPrice = ref<number>(null);

    const setOnlinePayment = (onlinePaymentData: IPaymentData) => {
        onlinePayment.value = onlinePaymentData;
        if (mapRequestPrice.value !== null) {
            onlinePayment.value.totalPrice += mapRequestPrice.value;
        }
    };

    const setHasOnlinePaymentError = (value: boolean) => {
        onlinePaymentError.value = value;
    }

    const isTopUpPayment = computed<boolean>(() => {
        return onlinePayment.value !== null && onlinePayment.value.paymentType === PaymentType.Topup;
    });

    const isOnlinePayment = computed<boolean>(() => {
        return onlinePayment.value !== null && onlinePayment.value.paymentType === PaymentType.Online;
    });

    const setHasTopUpPaymentError = (value: boolean) => {
        onlinePaymentError.value = value;
    };

    const setHasUncertainPrepaidError = (value: boolean) => {
        unCertainTopUpPaymentError.value = value;
    };

    const isPaymentMessagesVisible = computed<boolean>(() => {
        return isOnlinePayment.value || isTopUpPayment.value || unCertainTopUpPaymentError.value || onlinePaymentError.value || onlinePaymentError.value;
    });

    const processPaymentStatus = async (forTopUp: boolean, shoppingCartPaymentStatus: ShoppingCartPaymentStatusResult): Promise<boolean> => {
        if (shoppingCartPaymentStatus.status === ShoppingCartPaymentStatus.Pending) {
            return false;
        }

        if (forTopUp) {
            if (shoppingCartPaymentStatus.status === ShoppingCartPaymentStatus.Paid) {
                setOnlinePayment({
                    paymentType: PaymentType.Topup,
                });
            } else if (shoppingCartPaymentStatus.status === ShoppingCartPaymentStatus.Error) {
                setHasTopUpPaymentError(true);
            }
        } else {
            if (shoppingCartPaymentStatus.status === ShoppingCartPaymentStatus.Paid) {
                setOnlinePayment({
                    paymentType: PaymentType.Online,
                    numberOfMapRequests: shoppingCartPaymentStatus.numberOfMapRequests,
                    hasMapRequestExtras: shoppingCartPaymentStatus.numberOfIndividualMapRequestExtras > 0,
                    hasMapRequestsWithImkl: shoppingCartPaymentStatus.mapRequestsWithImklDataIncluded,
                    totalPrice: null,
                });
            } else if (shoppingCartPaymentStatus.status === ShoppingCartPaymentStatus.Error) {
                setHasOnlinePaymentError(true);
            }
        }

        return true;
    }

    return {
        onlinePayment,
        onlinePaymentError,
        topUpPaymentError,
        isTopUpPayment,
        isOnlinePayment,
        setOnlinePayment,
        setHasOnlinePaymentError,
        setHasTopUpPaymentError,
        setHasUncertainPrepaidError,

        isPaymentMessagesVisible,

        processPaymentStatus,
    };
});
